import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GroupDetails } from '../configure-eligibility-brackets.component.model';
import { GamesService } from 'src/app/gamification/command-center/services/games.service';

@Component({
  selector: 'cv-bracket-view',
  templateUrl: './bracket-view.component.html',
  styleUrls: ['./bracket-view.component.scss']
})

export class BracketViewComponent implements OnInit, OnChanges {
  @Input() data: any;
  @Input() numberOfRounds: number;
  @Input() isNewGame: boolean | null;
  @Input() loading: boolean | null = null;
  @Input() currentUser: any = null;
  @Input() selectedMetrics: any = null
  @Input() assignedGroups = null;
  @Input() rounds = null;
  @Input() gameStarted = false;

  leftBracket: GroupDetails[][] = [];
  rightBracket: GroupDetails[][] = [];
  hasWinner: boolean = false;
  winner: GroupDetails | null = null;
  firstLoading: boolean = false;
  ranking: any;
  isChange: boolean = false;

  constructor(
    private gamesService: GamesService
  ) { }
  ngOnInit() {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.isChange = true;
      this.processData();
    }
  }
  async processData() {
    if (this.data) {
      this.processRounds();
      const leftParticipants: GroupDetails[] = this.data.leftSide.flat();
      const rightParticipants: GroupDetails[] = this.data.rightSide.flat();

      const sortedLeftParticipants = this.sortParticipantsByRoundID(leftParticipants);
      const sortedRightParticipants = this.sortParticipantsByRoundID(rightParticipants);

      if ((this.isNewGame && !this.firstLoading) || (this.isChange && this.currentUser === null)) {
        await this.getParentName(sortedLeftParticipants, sortedRightParticipants);
        this.firstLoading = true;
        this.isChange = false;
      }

      this.leftBracket = this.organizeGroupsByRound(sortedLeftParticipants);
      this.rightBracket = this.organizeGroupsByRound(sortedRightParticipants);

      this.checkForWinner(sortedLeftParticipants, sortedRightParticipants);
    }
  }

  organizeGroupsByRound(participants: GroupDetails[]): GroupDetails[][] {
    const roundMap: { [key: number]: GroupDetails[] } = {};

    participants.forEach(group => {
      const roundId = group.RoundID || 0;
      if (!roundMap[roundId]) {
        roundMap[roundId] = [];
      }
      roundMap[roundId].push(group);
    });

    const sortedRoundIds = Object.keys(roundMap)
      .map(Number)
      .sort((a, b) => b - a);

    const organizedRounds: GroupDetails[][] = [];

    sortedRoundIds.forEach(roundId => {
      const groupsInRound = roundMap[roundId] || [];
      const expectedGroupCount = Math.pow(2, this.numberOfRounds - sortedRoundIds.indexOf(roundId) - 1);

      if (groupsInRound.length < expectedGroupCount) {
        const emptySlots = expectedGroupCount - groupsInRound.length;
        for (let i = 0; i < emptySlots; i++) {
          groupsInRound.push({} as GroupDetails);
        }
      }

      organizedRounds.push(groupsInRound);
    });

    while (organizedRounds.length < this.numberOfRounds) {
      organizedRounds.push(new Array(Math.pow(2, this.numberOfRounds - organizedRounds.length - 1)).fill({} as GroupDetails));
    }

    return organizedRounds;
  }

  sortParticipantsByRoundID(participants: GroupDetails[]): GroupDetails[] {
    return participants.filter(group => group && group.RoundID !== undefined)
      .sort((a, b) => (a.RoundID || 0) - (b.RoundID || 0))
      .concat(participants.filter(group => !group || group.RoundID === undefined));
  }
  getImageSrc(group: GroupDetails | null, size: number = 52): string {
    if (!group) return `../Admin/ImageResizer.aspx?imgid=${null}&w=${size}&h=${size}`;
    if (!group.UseAvatarImage && group.ImageID > 0) return `../Admin/ImageResizer.aspx?imgid=${group.ImageID}&w=${size}&h=${size}`; 
    if (group.UseAvatarImage && group.LoginID > 0) return `../Admin/AvatarProfileImage.aspx?loginid=${group.LoginID}&w=${size + 100}&h=${size + 100}`; 
    return `../Admin/ImageResizer.aspx?imgid=${null}&w=${size}&h=${size}`; // width = height = size
  }

  getMarginSize(rounds: number): string {
    if (rounds === 2) {
      return '18px';
    } else if (rounds === 3) {
      return '0px';
    } else if (rounds === 4) {
      return '26px';
    }
    return '0px';
  }

  checkForWinner(leftParticipants: GroupDetails[], rightParticipants: GroupDetails[]) {
    const lastRoundLeft = leftParticipants.find(participant => participant.RoundNumber === 1 && participant.isWinner);
    const lastRoundRight = rightParticipants.find(participant => participant.RoundNumber === 1 && participant.isWinner);

    if (lastRoundLeft || lastRoundRight) {
      this.hasWinner = true;
      this.winner = lastRoundLeft || lastRoundRight;
    } else {
      this.hasWinner = false;
      this.winner = null;
    }
  }

  isGroupEmpty(group: GroupDetails): boolean {
    return Object.keys(group).length === 0;
  }

  evaluateGroups(group: GroupDetails, round: GroupDetails[]): boolean {
    const isMainGroupEmpty = this.isGroupEmpty(group);

    const containsEmpty = round.some(g => this.isGroupEmpty(g));
    const containsNonEmpty = round.some(g => !this.isGroupEmpty(g));

    const containsBoth = containsEmpty && containsNonEmpty;

    return round.length > 1 && isMainGroupEmpty && containsBoth;
  }

  shouldHighlightCurrentUser(group: GroupDetails) {
      return this.currentUser && group.GroupName === this.currentUser.GroupName && group.RoundNumber === this.currentUser.RoundNumber &&
          (this.currentUser.RoundNumber !== 1 || (this.currentUser.RoundNumber === 1 && !this.currentUser.isWinner));
  }

  async getParentName(leftParticipants: GroupDetails[], rightParticipants: GroupDetails[]) {
    for (let group of leftParticipants) {
      let parentGroupName = null;
      if (group && group.GroupID !== undefined && group.Group2ID !== undefined) {
        parentGroupName = await this.gamesService.GetParentGroupName(group.GroupID, group.Group2ID, group.GroupLevel, group.BaseGroupType);
      }
      group.ParentEmpGroupName = parentGroupName?.ParentGroupName ?? '';
      group.ImageID = parentGroupName?.ImageID ?? 0;
      group.UseAvatarImage = parentGroupName?.UseAvatarImage;
      group.LoginID = parentGroupName?.LoginID;
    }
    for (let group of rightParticipants) {
      let parentGroupName = null;
      if (group && group.GroupID !== undefined && group.Group2ID !== undefined) {
        parentGroupName = await this.gamesService.GetParentGroupName(group.GroupID, group.Group2ID, group.GroupLevel, group.BaseGroupType);
      }
      group.ParentEmpGroupName = parentGroupName?.ParentGroupName ?? '';
      group.ImageID = parentGroupName?.ImageID ?? 0;
      group.UseAvatarImage = parentGroupName?.UseAvatarImage;
      group.LoginID = parentGroupName?.LoginID;
    }
  }

  async processRounds() {
    const currentDate = new Date();

    if (this.rounds && this.rounds.length > 0) {
      const finishedRounds = this.rounds.filter(round => new Date(round.EndDate) < currentDate);

      for (let round of finishedRounds) {
        const { StartDate, EndDate, RoundID } = round;

        await this.obtainParticipantsRanking(StartDate, EndDate);

        const updateMetricValues = (matchArray: any[]) => {
          matchArray.forEach(matchRound => {
            if (Array.isArray(matchRound)) {
              matchRound.forEach(match => {
                if (match.RoundID === RoundID) {
                  let participant = this.ranking.find(p => p.Group.GroupID === match.GroupID);
                  if (participant) {
                    match.metricValue = participant.MetricData.MetricDisplayValue;
                  }
                }
              });
            }
          });
        };

        if (this.data) {
          if (Array.isArray(this.data.rightSide)) {
            updateMetricValues(this.data.rightSide);
          }
          if (Array.isArray(this.data.leftSide)) {
            updateMetricValues(this.data.leftSide);
          }
        }
      }
    }
  }

  async obtainParticipantsRanking(startDate: string = null, endDate: string = null) {
    if (this.selectedMetrics && this.selectedMetrics[0]?.metricID) {
      const metricID = this.selectedMetrics[0].metricID;
      let ranking = await this.gamesService.LoadMetricRanking(metricID, this.assignedGroups.emp, startDate, null);
      this.ranking = ranking.RowData;
    }
  }
}
